import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTier } from "@/lib/ageGroup";
import { track } from "@/lib/track";
import { useLiveToday, timeLeft, impression } from "@/lib/liveToday";
import { SectionHead } from "@/components/home/HomeDiscoveryWorlds";

const STYLE = {
  LIVE_QUEST: { e: "🌍", kicker: "Quest", cta: "Join the quest", bg: "from-sky-200 via-emerald-100 to-yellow-100" },
  DAILY_CHALLENGE: { e: "⚡", kicker: "Daily challenge", cta: "Take the challenge", bg: "from-amber-200 via-yellow-100 to-white" },
  TOURNAMENT: { e: "🏆", kicker: "Tournament", cta: "Enter the tournament", ctaShort: "Enter", bg: "from-violet-200 via-fuchsia-100 to-white" },
};

/** Card copy derived ONLY from the registry item + the shared clock; returning-learner state replaces "new" framing. */
function present(i) {
  const s = STYLE[i.type];
  if (i.completed) {
    const done = i.type === "TOURNAMENT" ? { title: "You've entered this week's tournament", when: timeLeft(i.endAt) || "", cta: "See the standings" } : { title: "Today's challenge — done!", when: "Review your result or explore another challenge", cta: "Review your result" };
    return { ...s, ...done, live: false, kicker: `${s.kicker} · completed` };
  }
  if (i.type === "LIVE_QUEST") return { ...s, title: i.title, when: i.shortDescription, cta: i.status === "ACTIVE" ? s.cta : "Open Live Quest", live: i.status === "ACTIVE" };
  if (i.type === "DAILY_CHALLENGE") return { ...s, title: i.title, when: i.shortDescription, live: true };
  return { ...s, title: i.title, when: [timeLeft(i.endAt), i.shortDescription].filter(Boolean).join(" · "), live: true };
}

function Card({ item, primary = false }) {
  useEffect(() => { impression(item); }, [item.liveActivityId]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = present(item);
  return (
    <Link to={item.route} data-testid={`home-live-${item.activityId}`} data-status={item.status} data-completed={item.completed ? "1" : "0"} data-emphasis={primary ? "primary" : "secondary"}
      onClick={() => { track("home_live_clicked", { id: item.activityId }); track("live_activity_start", { id: item.liveActivityId, type: item.type, completed: !!item.completed }); }}
      className={`relative overflow-hidden rounded-3xl border-[3px] border-slate-900 bg-gradient-to-br ${p.bg} ${primary ? "p-4 col-span-full w-full" : "p-3 md:p-4 w-full h-full"} flex items-center gap-3 ${primary ? "shadow-[0_5px_0_0_rgba(15,23,42,1)]" : "shadow-[0_4px_0_0_rgba(15,23,42,1)]"} hover:-translate-y-0.5 transition-transform`}>
      <span aria-hidden className="absolute -right-6 -bottom-8 text-[88px] leading-none opacity-10 select-none z-0 pointer-events-none">{p.e}</span>
      <span aria-hidden className="relative z-10 text-3xl md:text-4xl leading-none shrink-0">{p.e}</span>
      <span className="relative z-10 min-w-0 flex-1">
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-rose-700">{p.live && <span className="px-1 rounded bg-rose-600 text-white">LIVE</span>} {p.kicker}</span>
        <span className={`block font-heading font-bold leading-tight ${primary ? "text-base md:text-xl" : "text-sm md:text-base"}`}>{p.title}</span>
        {p.when && <span className="block text-[11px] text-slate-700" data-testid={`home-live-${item.activityId}-when`}>{p.when}</span>}
        <span className="mt-1.5 inline-flex items-center min-h-[36px] px-3 rounded-full border-2 border-slate-900 bg-white text-xs font-bold whitespace-nowrap" aria-label={`${p.cta} →`}>
          {p.ctaShort ? <><span className="sm:hidden" aria-hidden>{p.ctaShort} →</span><span className="hidden sm:inline" aria-hidden>{p.cta} →</span></> : <>{p.cta} →</>}
        </span>
      </span>
    </Link>
  );
}

/**
 * LIVE TODAY — content-driven: renders exactly the items the Live Content Registry says are current
 * (0 → section hidden · 1 → full width · 2 → two columns · 3 → three). Dates, counts and countdowns come from the registry + server clock.
 */
export default function LiveTodayStrip() {
  const tier = useTier() || "explorers";
  const { items } = useLiveToday(tier);
  const active = items.filter((i) => i.status === "ACTIVE");
  if (!active.length) return null;
  const shown = active; // idle "Host or join a Live Quest" card removed from the home page (Oct 2026); only genuinely live items show
  // Primary = the genuinely ACTIVE event ending soonest (registry endAt); otherwise Live Quest. Event logic untouched.
  const timed = active.filter((i) => i.endAt && !i.completed).sort((a, b) => new Date(a.endAt) - new Date(b.endAt));
  const primaryId = (timed[0] || shown.find((i) => i.type === "LIVE_QUEST") || shown[0])?.liveActivityId;
  const ordered = [...shown].sort((a, b) => (a.liveActivityId === primaryId ? -1 : b.liveActivityId === primaryId ? 1 : 0));
  const cols = shown.length === 1 ? "grid-cols-1" : "grid-cols-1 min-[480px]:grid-cols-2";
  return (
    <section className="mt-7 md:mt-9" data-testid="home-live-today" data-count={shown.length} aria-labelledby="home-live-title">
      <SectionHead id="home-live-title" dot title="Live today" sub="Optional ways to practise, challenge yourself and show what you know" />
      <div className={`mt-3 grid ${cols} gap-2.5 md:gap-3 min-w-0`} data-testid="home-live-strip-row">
        {ordered.map((i) => <Card key={i.liveActivityId} item={i} primary={shown.length > 2 && i.liveActivityId === primaryId} />)}
      </div>
    </section>
  );
}
